// wsClient.js
import { AUTH_TOKEN, REFRESH_TOKEN } from 'constants/AuthConstant';
import { API_BASE_URL } from 'configs/AppConfig';

let ws = null;
let listeners = new Set();
let reconnectTimer = null;
let pingInterval = null;
let lastUrl = null;
let isRefreshing = false;

// Ping every 25 seconds to keep connection alive (most servers timeout at 30-60s)
const PING_INTERVAL = 25000;
// Reconnect after 5 seconds on disconnect (longer to avoid spam)
const RECONNECT_DELAY = 5000;
// Max reconnect attempts before giving up
const MAX_RECONNECT_ATTEMPTS = 5;
let reconnectAttempts = 0;
// Track consecutive auth failures to stop spamming
let authFailures = 0;
const MAX_AUTH_FAILURES = 3;

function startPing() {
  stopPing();
  pingInterval = setInterval(() => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ type: "ping", t: Date.now() }));
      } catch (e) {
        console.log("[ws] ping failed:", e.message);
      }
    }
  }, PING_INTERVAL);
}

function stopPing() {
  if (pingInterval) {
    clearInterval(pingInterval);
    pingInterval = null;
  }
}

// Get current token from localStorage (always fresh)
function getCurrentToken() {
  return localStorage.getItem(AUTH_TOKEN);
}

// Try to refresh the token before reconnecting
async function tryRefreshToken() {
  if (isRefreshing) return false;
  
  const refreshToken = localStorage.getItem(REFRESH_TOKEN);
  const accessToken = getCurrentToken();
  
  if (!refreshToken) {
    console.log("[ws] no refresh token, cannot refresh");
    return false;
  }
  
  isRefreshing = true;
  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ refreshToken }),
    });
    
    if (!response.ok) {
      console.log("[ws] token refresh failed:", response.status);
      return false;
    }
    
    const data = await response.json();
    if (data.token) {
      localStorage.setItem(AUTH_TOKEN, data.token);
      if (data.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN, data.refreshToken);
      }
      console.log("[ws] token refreshed successfully");
      authFailures = 0; // Reset auth failures on successful refresh
      return true;
    }
    return false;
  } catch (err) {
    console.log("[ws] token refresh error:", err.message);
    return false;
  } finally {
    isRefreshing = false;
  }
}

export function ensureWS(url, token) {
  // Store URL only, always get fresh token on reconnect
  lastUrl = url;

  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
    return ws;
  }

  // Use provided token or get current token from localStorage
  const currentToken = token || getCurrentToken();
  
  if (!currentToken) {
    console.log("[ws] no token available, skipping connection");
    return null;
  }
  
  // Don't connect if we've had too many auth failures
  if (authFailures >= MAX_AUTH_FAILURES) {
    console.log("[ws] too many auth failures, not connecting");
    return null;
  }

  ws = new WebSocket(url, currentToken);

  ws.onopen = () => {
    console.log("[ws] singleton connected");
    reconnectAttempts = 0; // Reset on successful connection
    authFailures = 0; // Reset auth failures on successful connection
    startPing(); // Start keepalive pings
  };
  
  ws.onmessage = (ev) => listeners.forEach(fn => fn(ev));
  ws.onerror = (e) => console.log("[ws] singleton error", e);

  ws.onclose = async (e) => {
    console.log("[ws] singleton closed, code:", e.code);
    stopPing();
    clearTimeout(reconnectTimer);
    ws = null;
    
    // Code 1008 = Policy Violation (auth failure like expired token)
    const isAuthFailure = e.code === 1008;
    
    if (isAuthFailure) {
      authFailures++;
      console.log("[ws] auth failure count:", authFailures);
      
      // Try to refresh token on auth failure
      if (authFailures < MAX_AUTH_FAILURES) {
        const refreshed = await tryRefreshToken();
        if (refreshed && lastUrl) {
          console.log("[ws] reconnecting with fresh token...");
          reconnectTimer = setTimeout(() => {
            ensureWS(lastUrl, getCurrentToken());
          }, 1000);
          return;
        }
      }
      
      if (authFailures >= MAX_AUTH_FAILURES) {
        console.log("[ws] too many auth failures, giving up");
        return;
      }
    }
    
    // Check if we should reconnect
    const currentToken = getCurrentToken();
    if (!currentToken) {
      console.log("[ws] no token, not reconnecting (user logged out)");
      return;
    }
    
    // Limit reconnect attempts
    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.log("[ws] max reconnect attempts reached, giving up");
      return;
    }
    
    // Exponential backoff: 5s, 10s, 20s, 40s, etc. (max 60s)
    const delay = Math.min(RECONNECT_DELAY * Math.pow(2, reconnectAttempts), 60000);
    reconnectAttempts++;
    
    console.log("[ws] retrying in", delay, "ms (attempt", reconnectAttempts, ")");
    reconnectTimer = setTimeout(() => {
      if (lastUrl) ensureWS(lastUrl, getCurrentToken()); // Always use fresh token
    }, delay);
  };

  return ws;
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Force reconnect (useful when token changes)
export function forceReconnect() {
  stopPing();
  clearTimeout(reconnectTimer);
  reconnectAttempts = 0; // Reset attempts on manual reconnect
  if (ws) {
    try { ws.close(); } catch {}
  }
  ws = null;
  if (lastUrl) {
    ensureWS(lastUrl, getCurrentToken()); // Use fresh token
  }
}

// Check if connected
export function isConnected() {
  return ws && ws.readyState === WebSocket.OPEN;
}

// Disconnect completely (for logout)
export function disconnect() {
  stopPing();
  clearTimeout(reconnectTimer);
  reconnectAttempts = MAX_RECONNECT_ATTEMPTS; // Prevent auto-reconnect
  if (ws) {
    try { ws.close(); } catch {}
  }
  ws = null;
  lastUrl = null;
}
